import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { AppointmentService as SharedAppointmentService } from '@avanta/shared/services/appointments';
import { type EditAppointmentPayload } from '@avanta/shared/types/appointments';
import { useMutation, type UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { type AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { MUTATION_KEYS, QUERY_KEYS } from 'constants/common';
import { ROUTES } from 'constants/routes';
import { AppointmentService } from 'services/appointments';
import { useUserStore } from 'stores/user';

export const useEditAppointment = (): UseMutationResult<void, Error, EditAppointmentPayload> => {
  const queryClient = useQueryClient();
  const isPsychologist = useUserStore(state => state.isPsychologist);
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const navigate = useNavigate();
  const { state } = useLocation();
  const from = state?.from;

  return useMutation<void, AxiosError, EditAppointmentPayload>({
    mutationKey: [MUTATION_KEYS.UPDATE_APPOINTMENT],
    mutationFn: async (body: EditAppointmentPayload) => {
      if (isPsychologist) {
        await AppointmentService.editFromPsychologist(body);
        return;
      }
      await SharedAppointmentService.edit(body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FETCH_NEXT_APPOINTMENTS] });
      toast.success(t('EditAppointment.success'));
      if (isPsychologist) {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FETCH_PATIENT_APPOINTMENTS] });
        navigate(from);
        return;
      }
      navigate(ROUTES.APPOINTMENTS);
    },
    onError: error => {
      //TODO usar custom error code
      if (error?.status === 403) {
        toast.error(t('EditAppointment.error.forbidden'));
      } else {
        toast.error(t('EditAppointment.error.generic'));
      }
    },
  });
};
